/*eslint react-hooks/exhaustive-deps: "off"*/
/*eslint no-unused-vars: "off"*/
import React, {useCallback, useEffect} from 'react';
import MapBase from "./MapBase";
import useMapProps from "./useMapProps";
import {MapPropsProvider} from "./MapPropsContext";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import {
  formatServiceCode,
  formatUid,
  getApiUrl,
  getReadableFreq,
  makeTableSorterFn,
  positionToDegress
} from "../../utils";
import WidgetTable from "./widgets/WidgetTable";
import WidgetContainer from "./widgets/WidgetContainer";
import WidgetCellularCommon from "./widgets/WidgetCellularCommon";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetToolbar from "./widgets/WidgetToolbar";
import {MINT_COLOR} from "../../utils/Palette";
import AlignRightText from "./widgets/AlignRightText";
import AlignCenterText from "./widgets/AlignCenterText";
import EllipsisText from "./widgets/EllipsisText";
import WidgetUidWithShortcut from "./widgets/WidgetUidWithShortcut";
import {Tooltip} from "antd";


// 공용화가능무선국
const ICON_MAPPING = {
  'SK': {
    '2G': ['2g-sk-out', 'SK 2G'],
    '3G': ['3g-sk-out', 'SK 3G'],
    '4G': ['4g-sk-out', 'SK 4G'],
  },
  'KT': {
    '3G': ['3g-kt-out', 'KT 3G'],
    '4G': ['4g-kt-out', 'KT 4G'],
    // 'Wibro': ,  // 제거
  },
  'LG': {
    '3G': ['3g-lg-out', 'LG 3G'],
    '4G': ['4g-lg-out', 'LG 4G'],
  },
};
const ICON_LEGEND = [
  ['5g-sk-out-none', 'SKT'],
  ['5g-kt-out-none', 'KT'],
  ['5g-lg-out-none', 'LGU+'],
];

const CUSTOMER_NO_DICT = {
  'SK': [542604, 2390, 48805, 541309, 521297, 195403],
  'KT': [542610, 2151, 33141, 42233, 83943, 89987],
  'LG': [39818, 259976],
};

// const ICON_LEGEND = Object.values(ICON_MAPPING).map(row => Object.values(row)).flat();

const MapCellularCommon = () => {
  const mapProps = useMapProps({
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: (feature) => {
        let {customer_name_abbr, service_code_abbr, ungb, service_code, customer_no} = feature;
        if (service_code_abbr === 'Wibro')
          return null;
        // FIXME: ungb 값에 대한 검증이 필요합니다.
        if (ungb && ungb.length === 4) {
          customer_name_abbr = ungb.substr(0, 2);
          service_code_abbr = ungb.substr(2, 2) || '';
        }
        if(!service_code_abbr || !customer_name_abbr){
          if(service_code === '34' || service_code === '16')
            service_code_abbr = 'Wibro';
          if(service_code === '05' || service_code === "06" || service_code === '31' || service_code === '33')
            service_code_abbr = '2G';
          if(service_code === '15' || service_code === '32')
            service_code_abbr = '3G';
          if(service_code === '18' || service_code === '19' || service_code === '36')
            service_code_abbr = '4G';

          customer_name_abbr = Object.entries(CUSTOMER_NO_DICT)
            .filter(([vendorName, customerNoList]) =>
              customerNoList.includes(customer_no)
            )
            .map(([vendorName]) => vendorName)[0];
        }

        try {
          return ICON_MAPPING[customer_name_abbr][service_code_abbr][0];
        } catch (e) {
          // console.error("not found iconCode for", customer_name_abbr, service_code_abbr);
          return null;
        }
      },
    },
    INITIAL_GEOJSON_EXTRA_PARAMS: {
      currentDistrictCode: '11680',
    },
    INITIAL_WIDGET_STATE: {
      customer_name_abbr_list: ["SK", "LG", "KT"],
      terrain_altitude_list: ["A", "B"],
      form_code_list: ["1", "2", "3", "8", "10"],
      imCellularCommonSido : false,
    },
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getLevelFillColor: () => {
        return MINT_COLOR;
      },
    }
  });


  const {
    clickedPointIconObjects,
    setClickedPointIconObjects,
    widgetState,
    setPointsLayerFilterFunc,
    currentDistrictCode,
    setGeojsonExtraParams,
    clickedPolygonGeojsonObjects,
    setCurrentDistrictCode,
    setNotAllAreaPolygon,
  } = mapProps;

  useEffect(() => {
    setNotAllAreaPolygon(true);
  }, []);

  useEffect(() => {
    setGeojsonExtraParams(prev => ({
      ...prev,
      currentDistrictCode,
    }));
  }, [currentDistrictCode]);

  useEffect(() => {
    const {
      customer_name_abbr_list,
      terrain_altitude_list,
      form_code_list,
      search_uid
    } = widgetState;

    if (search_uid) {
      setPointsLayerFilterFunc(() => ({uid}) => {
        if (uid === search_uid) return true;
      })
    } else {
      setPointsLayerFilterFunc(() => ({customer_name_abbr, form_code, terrain_altitude}) => {
        if (!customer_name_abbr_list.includes(customer_name_abbr))
          return false;
        if (!form_code_list.includes(form_code))
          return false;
        if (terrain_altitude < 30 && terrain_altitude_list.includes("A"))
          return true;
        if (terrain_altitude >= 30 && terrain_altitude_list.includes("B"))
          return true;
        return false;
      });
    }
  }, [widgetState]);

  useEffect(() => {
    if (clickedPolygonGeojsonObjects.length > 0) {
      const firstLayer = clickedPolygonGeojsonObjects[0];
      // eslint-disable-next-line no-unused-vars
      const {properties: {name, pk}} = firstLayer;
      // if(pk.length !== 2)
        setCurrentDistrictCode(pk);
    }
  }, [clickedPolygonGeojsonObjects]);

  const onClickedPointInWidgetTable = useCallback((feature) => {
    const {latitude, longitude, lat, lon} = feature;
    const newLatitude = positionToDegress(latitude || lat);
    const newLongitude = positionToDegress(longitude || lon);
    const newFeature = {...feature, position: [newLongitude, newLatitude]};
    setClickedPointIconObjects([newFeature]);
  }, [setClickedPointIconObjects]);


  return (
    <MapPropsProvider value={mapProps}>
      <MapBase>
        <WidgetContainer
          // onChange={tabTitle => {
          //   if ( tabTitle === '허가번호 검색' ) {
          //     setSearchByUidForMapViewType();
          //     setClickedPointIconObjects([]);
          //   }
          //   else {
          //     setNullForMapViewType();
          //   }
          // }}
        >
          <div title={"주소 검색"}>
            <WidgetDistrictSelect isShowDistrictLayerFromGeoserver={true}
                                  injectDistrictLv2Value={"11680"}  // 서울 강남구
                                  areUcellularCommon={true}
                                  noAllLv1={true}
            />
            <WidgetCellularCommon/>
          </div>
          {/*<div title={"허가번호 검색"}>*/}
          {/*  <WidgetSearchByUid*/}
          {/*    fetchUrl={getApiUrl({type: "type3"})}*/}
          {/*  />*/}
          {/*</div>*/}
        </WidgetContainer>

        <WidgetTableClicked
          fetchUrl={getApiUrl({type: "type2"})}
          getTitle={objects => `${objects.length}개의 공용화 가능 무선국`}
          objects={clickedPointIconObjects}
          getRowTitle={({uid, rds_svc_form_cd}) => `${formatUid(uid)} (${formatServiceCode(rds_svc_form_cd)})`}
          getRow={({uid, rds_cll_nm, cus_nm, rds_trs_adr, rds_svc_form_nm}) =>
            <table>
              <tbody>
              <tr>
                <th>허가번호</th>
                <td>{formatUid(uid)}</td>
              </tr>
              <tr>
                <th>호출명칭</th>
                <td>{rds_cll_nm}</td>
              </tr>
              <tr>
                <th>시설자명</th>
                <td>{cus_nm}</td>
              </tr>
              <tr>
                <th>설치장소</th>
                <td>{rds_trs_adr}</td>
              </tr>
              <tr>
                <th>용도</th>
                <td>{rds_svc_form_nm}</td>
              </tr>
              </tbody>
            </table>
          }
        />

        <WidgetTable
          fetchUrl={getApiUrl({type: "type1"})}
          getTitle={objects => `${objects.length}개의 공용화 가능 무선국`}
          isUidSearch={true}
          columns={[
            {title: '순번', dataIndex: 'rnum', key: 'rnum', width: 75, align: 'center', sorter: makeTableSorterFn('rnum'), fixed: 'left'},
            {
              title: '허가번호', dataIndex: 'uid', key: 'uid', width: 200,
              render: (uid, feature) => WidgetUidWithShortcut({feature, onClick: onClickedPointInWidgetTable}),
              sorter: makeTableSorterFn("uid"), fixed: 'left'
            },
            {
              title: '호출명칭', dataIndex: 'rds_cll_nm', key: 'rds_cll_nm', width: 150
              , render: rds_cll_nm => <EllipsisText text={rds_cll_nm||"-"}/>,
            },
            {title: '시설자명', dataIndex: 'cus_nm', key: 'cus_nm', width: 240, sorter: makeTableSorterFn("cus_nm")},
            {
              title: '설(상)치장소', dataIndex: 'rds_trs_adr', key: 'rds_trs_adr', width: 500
              , render: rds_trs_adr => <EllipsisText text={rds_trs_adr}/>
              , sorter: makeTableSorterFn("rds_trs_adr")
            },
            {
              title: '용도', dataIndex: 'rds_svc_form_nm', key: 'rds_svc_form_nm', width: 400
              , render: rds_svc_form_nm => <EllipsisText text={rds_svc_form_nm}/>
              , sorter: makeTableSorterFn("rds_svc_form_nm")
            },
            {
              title: '장치개수', dataIndex: 'rds_eqm_snu', key: 'rds_eqm_snu', width: 100,
              render: text => AlignRightText(({text})),
              sorter: makeTableSorterFn("rds_eqm_snu")
            },
            {
              title: '인증번호', dataIndex: 'toap_no', key: 'toap_no', width: 230
              , render: toap_no => <EllipsisText text={toap_no}/>
              , sorter: makeTableSorterFn("toap_no")
            },
            {
              title: '경도', dataIndex: 'lon', key: 'lon', width: 130,
              render: text => AlignCenterText({text}),
            },
            {
              title: '위도', dataIndex: 'lat', key: 'lat', width: 130,
              render: text => AlignCenterText({text}),
            },
            {
              title: '주파수', dataIndex: 'frq_hz', key: 'frq_hz', width: 120,
              render: frq_hz =>
                  frq_hz && frq_hz.toString().split(',').map(frq => {
                    return AlignCenterText({
                      children:
                          <Tooltip title={frq} placement={"top"}>
                            {getReadableFreq(frq)}
                          </Tooltip>
                    })
                  }),
              sorter: makeTableSorterFn("frq_hz")
            },
            {
              title: '출력', dataIndex: 'arw_pwr_wtt', key: 'arw_pwr_wtt', width: 100,
              render: text => AlignRightText({text}),
              sorter: makeTableSorterFn("arw_pwr_wtt")
            },
            {
              title: '안테나설치형태', dataIndex: 'arw_nm', key: 'arw_nm', width: 140,
              sorter: makeTableSorterFn("arw_nm")
            },
            {
              title: '대표이득', dataIndex: 'arw_gan_nmv', key: 'arw_gan_nmv', width: 100,
              render: text => AlignRightText({text}),
              sorter: makeTableSorterFn("arw_gan_nmv")
            },
            {
              title: '해발고', dataIndex: 'arw_alt_altd_het', key: 'arw_alt_altd_het', width: 100,
              render: text => AlignRightText({text}),
              sorter: makeTableSorterFn("arw_alt_altd_het")
            },
            {
              title: '지상고', dataIndex: 'arw_gnd_altd_het', key: 'arw_gnd_altd_het', width: 100,
              render: text => AlignRightText({text}),
              sorter: makeTableSorterFn("arw_gnd_altd_het")
            },
            {
              title: '노출고', dataIndex: 'arw_exps_altd_het', key: 'arw_exps_altd_het', width: 100,
              render: text => AlignRightText({text}),
              sorter: makeTableSorterFn("arw_exps_altd_het")
            },
          ]}
          horizontalScroll={2650}
        />
        <WidgetLegend iconLegend={ICON_LEGEND}/>
      </MapBase>
      <WidgetToolbar/>
    </MapPropsProvider>
  );
};


export default MapCellularCommon;
